import { CheckBox, ErrorMessage, Input } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import type { LinkBankAccountFormValues } from './LinkBankAccount.types';
import styles from './LinkDEBankAccount.module.css';
import { normalizeIBANNumber } from '../../../../helpers/normalizers';
import { detectBankAccountLinked } from '../../../Profile/ProfileHelper';

const mapStateToProps = (state: RootState) => ({
    isPendingBankAccount: state?.api?.payment_method?.pending,
    hasLinkedBankAccount: detectBankAccountLinked(
        state?.api?.payment_method?.data
    )
});

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const LinkDEBankAccountForm = ({
    isPendingBankAccount,
    hasLinkedBankAccount
}: PropsFromRedux) => {
    const {
        control,
        formState: { errors }
    } = useFormContext<LinkBankAccountFormValues>();

    const ibanDisabled = hasLinkedBankAccount || isPendingBankAccount;

    return (
        <>
            <div className="relative w-full">
                <Controller
                    control={control}
                    name="iban"
                    render={({
                        field: { onChange, value, onBlur, name, ref }
                    }) => (
                        /* eslint-disable-next-line iwoca/input-a11y */
                        <Input
                            ref={ref}
                            name={name}
                            label={
                                <FormattedMessage
                                    id="_.modal.link_bank_account.iban"
                                    defaultMessage="IBAN"
                                />
                            }
                            data-testid="iban-input"
                            className="fs-exclude"
                            value={value ?? ''}
                            onBlur={onBlur}
                            onChange={(e) =>
                                onChange(normalizeIBANNumber(e.target.value))
                            }
                            disabled={ibanDisabled}
                            error={errors.iban?.message}
                        />
                    )}
                />
                {isPendingBankAccount ? (
                    <div
                        className={styles.pendingIBAN}
                        data-testid="iban-loading"
                    />
                ) : null}
            </div>

            <hr className={styles.divider} />

            <div className={styles.mandateTitle}>
                SEPA-Basislastschriftmandat für wiederkehrende Zahlungen
            </div>

            <Controller
                control={control}
                name="directDebitMandate"
                render={({ field: { onChange, value, name, ref } }) => (
                    <div className={styles.mandateCheckbox}>
                        <label className="flex items-start gap-m">
                            <CheckBox
                                ref={ref}
                                name={name}
                                data-testid="direct-debit-mandate-checkbox"
                                checked={!!value}
                                onChange={(e) => onChange(e.target.checked)}
                            />
                            <div className="flex flex-col gap-m">
                                <span>
                                    Ich ermächtige iwoca Management S.à.r.l.
                                    (Gläubiger-ID: LU72ZZZ0000000000000001848)
                                    und Stripe Payments Europe Ltd., unseren
                                    Zahlungsdienstleister, Zahlungen von meinem
                                    oben angegebenen Konto mittels Lastschrift
                                    einzuziehen. Zugleich weise ich mein
                                    Kreditinstitut an, die von Stripe Payments
                                    Europe Ltd. im Auftrag von iwoca Management
                                    S.à.r.l. auf mein Konto gezogenen
                                    Lastschriften einzulösen.
                                    <br />
                                    <br />
                                    Weiterhin erkläre ich mich damit
                                    einverstanden, die Vorabankündigung bis
                                    spätestens 3 Tage vor Zahlungseinzug zu
                                    erhalten. Meine Rechte kann ich den
                                    Bedingungen meines Kreditinstituts
                                    entnehmen.
                                    <br />
                                    <br />
                                    Hinweis: Ich kann innerhalb von acht Wochen,
                                    beginnend mit dem Belastungsdatum, die
                                    Erstattung des belasteten Betrages
                                    verlangen. Es gelten dabei die mit meinem
                                    Kreditinstitut vereinbarten Bedingungen.
                                    <br />
                                    <br />
                                    Für mehr Informationen lesen Sie gern auch
                                    den{' '}
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href="https://stripe.com/en-gb-de/privacy#translation"
                                    >
                                        Datenschutzhinweis
                                    </a>{' '}
                                    von Stripe.
                                </span>
                                {errors.directDebitMandate?.message ? (
                                    <ErrorMessage.Root className="items-start">
                                        <ErrorMessage.Icon className="mt-[2px]" />
                                        <ErrorMessage.Text>
                                            {errors.directDebitMandate.message}
                                        </ErrorMessage.Text>
                                    </ErrorMessage.Root>
                                ) : null}
                            </div>
                        </label>
                    </div>
                )}
            />
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(LinkDEBankAccountForm);
